import React, { useState } from 'react';
import { connect, useDispatch } from 'react-redux';
import PropTypes from 'prop-types';
import Modal from '../Modal';
import Button from '../Button';
import Icon from '../Icon';
import SearchResult from '../SearchResult';
import TranslationProvider, { getTranslation } from '../../providers/TranslationProvider';
import {
  MODAL_MODES,
  setMultiStepModalMode,
  setMultiStepModalOpenState,
  setMultiStepModalVenueId,
  setMultiStepModalVenueContext,
} from '../../reducers/favoriteSlice';
import Close from '../../../../consumer_web/app/modules/svg/dynamic/close.svg';
import BrokenHeart from '../../svg/icons/ui-favorite.svg';
import PeopleIcon from '../../svg/icons/ic_guests_20x20.svg';

import './NotifyUnavailableModal.scss';
import useGdaNotificationData from '../NotifyGdaPage/useGdaNotificationData';
import { CONSTANTS } from '../Notify/notifyUtils';
import NotifyGdaModal from '../NotifyGdaPage/NotifyGdaModal';

const NotifyUnavailableModal = ({
  $rootScope,
  Analytics,
  isMultiStepModalOpen,
  notifyDate,
  notifyPartySize,
  notifyStartTime,
  notifyEndTime,
  notifyTableType,
  notifyVenueName,
  venueGroupIds,
  venues,
  isGdaUser,
  matchMedia,
  locationCode,
  deviceId,
}) => {
  const dispatch = useDispatch();
  const [isOpen, setIsOpen] = useState(true);
  const [gdaVisibility, setGdaVisibility] = useState(false);

  const { POST_NOTIFY } = CONSTANTS;
  const { data } = useGdaNotificationData({
    locationCode, deviceId, flowContext: POST_NOTIFY, fetchData: !isGdaUser,
  });

  Analytics.event('view recommendations', {
    context: 'recommendations modal',
  });

  const handleFavoriteClick = (response) => {
    Analytics.event('add to list', {
      context: 'recommendations modal',
    });

    const { venue_id: venueId, venue } = response;

    dispatch(setMultiStepModalVenueContext({ name: venue?.name, city: venue?.location?.locality, context: 'Notify unavailable' }));
    dispatch(setMultiStepModalVenueId(venueId));
    dispatch(setMultiStepModalMode(MODAL_MODES.ADD_TO_LIST));
    dispatch(setMultiStepModalOpenState(true));
  };

  const handleVenueClick = () => {
    Analytics.event('view page', {
      context: 'recommendations modal',
    });
  };

  const onClose = () => {
    setIsOpen(false);
  };

  const showGdaOrCloseModal = () => {
    if (data) {
      setGdaVisibility(true);
    } else {
      setIsOpen(false);
    }
  };

  if (isOpen && gdaVisibility && data) {
    return <NotifyGdaModal isOpen={isOpen} onClose={onClose} data={data} Analytics={Analytics} matchMedia={matchMedia} />;
  }

  return (
    <Modal
      isOpen={isOpen && !isMultiStepModalOpen}
      shouldCloseOnEsc
      shouldCloseOnOverlayClick
      hasFooter
      primaryCTAText={getTranslation({ translationKey: 'no_thanks', fallback: 'No Thanks' })}
      primaryCTAVariant="primary"
      primaryCTAAdditionalProps={{
        onClick: showGdaOrCloseModal,
        'data-test-id': 'notify-unavailable-primary-cta',
      }}
      styles={{
        content: {
          maxWidth: '700px',
        },
      }}
    >
      <div className="NotifyUnavailableModal">
        <div className="NotifyUnavailableModal__header">
          <Button
            onClick={onClose}
            variant="no-style"
            className="Button__close"
            aria-label={getTranslation({
              translationKey: 'close',
              fallback: 'Close',
            })}
            data-test-id="notify-unavailable-button-close"
          >
            <Close />
          </Button>
        </div>

        <div className="NotifyUnavailableModal__title-container">
          <TranslationProvider translationKey="notify_table_no_longer_available_title">
            <div className="NotifyUnavailableModal__title">
              Table no longer available
            </div>
          </TranslationProvider>
          <Icon component={BrokenHeart} name="broken-heart" />
        </div>

        <TranslationProvider translationKey="notify_table_no_longer_available">
          <div className="NotifyUnavailableModal__subtitle">Unfortunately, you didn&apos;t get this one, but you&apos;ll be notified if another table opens up.</div>
        </TranslationProvider>

        <hr className="NotifyUnavailableModal__hr" />

        <div className="NotifyUnavailableModal__card-container">
          <TranslationProvider translationKey="book_spots_like_venue" translationValues={{ venueName: notifyVenueName }}>
            <div className="NotifyUnavailableModal__book-similar-title">
              {`Book spots like ${notifyVenueName}`}
            </div>
          </TranslationProvider>
          <div className="NotifyUnavailableModal__notify-params">
            <span>{moment(notifyDate).format('ll')}</span>
            &nbsp;•&nbsp;
            <Icon component={PeopleIcon} name="people" />
            <span>{notifyPartySize}</span>
            &nbsp;•&nbsp;
            <span>
              {moment(notifyStartTime, 'HH:mm').format('hh:mm A')}
              {' '}
              -
              {' '}
              {moment(notifyEndTime, 'HH:mm').format('hh:mm A')}
            </span>
            {notifyTableType && (
              <>
                &nbsp;•&nbsp;
                <span>{notifyTableType}</span>
              </>
            )}
          </div>

          {venues?.map((venue) => (
            <SearchResult
              key={venue.id}
              $rootScope={$rootScope}
              canFavorite
              venue={venue}
              showNotify={false}
              onFavoriteClick={handleFavoriteClick}
              event={Analytics.event}
              onVenueClick={handleVenueClick}
              selectedDate={notifyDate}
              eyebrow={venueGroupIds.includes(venue.id) && getTranslation({
                translationKey: 'same_group_recommendations_eyebrow',
                translationValues: { name: venue.name },
                fallback: `From the ${venue.name} team`,
              })}
            />
          ))}
        </div>
      </div>
    </Modal>
  );
};

NotifyUnavailableModal.propTypes = {
  $rootScope: PropTypes.shape({
    $broadcast: PropTypes.func.isRequired,
  }).isRequired,
  Analytics: PropTypes.shape({
    event: PropTypes.func.isRequired,
  }).isRequired,
  isMultiStepModalOpen: PropTypes.bool.isRequired,
  notifyDate: PropTypes.string.isRequired,
  notifyPartySize: PropTypes.number.isRequired,
  notifyStartTime: PropTypes.string.isRequired,
  notifyEndTime: PropTypes.string.isRequired,
  notifyTableType: PropTypes.string,
  notifyVenueName: PropTypes.string.isRequired,
  venueGroupIds: PropTypes.arrayOf(PropTypes.number),
  venues: PropTypes.arrayOf(PropTypes.shape({
    sameGroup: PropTypes.bool,
    name: PropTypes.string,
  })).isRequired,
  isGdaUser: PropTypes.bool,
  matchMedia: PropTypes.shape({
    xs: PropTypes.bool,
    sm: PropTypes.bool,
    md: PropTypes.bool,
    lg: PropTypes.bool,
    ipadLandscape: PropTypes.bool,
    xl: PropTypes.bool,
  }).isRequired,
  locationCode: PropTypes.string,
  deviceId: PropTypes.string.isRequired,
};

NotifyUnavailableModal.defaultProps = {
  notifyTableType: undefined,
  venueGroupIds: [],
  isGdaUser: false,
  locationCode: undefined,
};

const mapStateToProps = ({
  favorite, matchMedia, resyLocations, resyConfig,
}) => ({
  isMultiStepModalOpen: favorite.isMultiStepModalOpen,
  matchMedia,
  locationCode: resyLocations?.selected?.code,
  deviceId: resyConfig?.deviceId,
});

NotifyUnavailableModal.displayName = 'NotifyUnavailableModal';
export { NotifyUnavailableModal as ComponentUnderTest };
export default connect(mapStateToProps)(NotifyUnavailableModal);
